// HEXA Offline Queue — captures failed mutating API calls when offline,
// replays them automatically when the network returns.
//
// Storage: localStorage (simple, synchronous, plenty for queue size).
// Listeners can subscribe to status changes for UI badges.

import axios from "axios";

const KEY = "hexa_offline_queue";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class OfflineBus {
  constructor() {
    this.listeners = new Set();
    this.online = (typeof navigator !== "undefined") ? navigator.onLine : true;
    this.syncing = false;
    if (typeof window !== "undefined") {
      window.addEventListener("online", () => this._setOnline(true));
      window.addEventListener("offline", () => this._setOnline(false));
    }
  }

  _setOnline(v) {
    this.online = v;
    this._emit();
    if (v) this.flush();
  }

  read() {
    try { return JSON.parse(localStorage.getItem(KEY) || "[]"); }
    catch { return []; }
  }

  write(arr) {
    try { localStorage.setItem(KEY, JSON.stringify(arr)); } catch { /* noop */ }
    this._emit();
  }

  count() { return this.read().length; }

  enqueue(req) {
    const arr = this.read();
    arr.push({ ...req, ts: Date.now(), id: Math.random().toString(36).slice(2, 9) });
    this.write(arr);
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  _emit() {
    const snap = { online: this.online, pending: this.count(), syncing: this.syncing };
    for (const l of Array.from(this.listeners)) {
      try { l(snap); } catch { /* noop */ }
    }
  }

  async flush() {
    if (this.syncing) return;
    let arr = this.read();
    if (arr.length === 0 || !this.online) return;
    this.syncing = true;
    this._emit();
    const token = (typeof localStorage !== "undefined") ? localStorage.getItem("aura_token") : null;
    const remaining = [];
    for (const item of arr) {
      try {
        await axios({
          method: item.method,
          url: `${BACKEND_URL}${item.url}`,
          data: item.data,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          timeout: 12000,
        });
      } catch (e) {
        // If status is a real backend response (4xx) we DROP the request
        // (the server rejected it — replaying won't help). Network errors get re-queued.
        if (e?.response) continue;
        remaining.push(item);
      }
    }
    this.write(remaining);
    this.syncing = false;
    this._emit();
  }

  /** Attempt the request live; on network failure, queue it. Returns true if it was queued. */
  async tryOrQueue({ method, url, data }) {
    const token = (typeof localStorage !== "undefined") ? localStorage.getItem("aura_token") : null;
    try {
      const r = await axios({
        method, url: `${BACKEND_URL}${url}`, data,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 8000,
      });
      return { queued: false, response: r };
    } catch (e) {
      if (!navigator.onLine || (!e.response && e.code !== "ERR_BAD_REQUEST")) {
        this.enqueue({ method, url, data });
        return { queued: true };
      }
      throw e;
    }
  }
}

export const offlineQueue = new OfflineBus();

// Wire the existing axios `api` instance: any mutating request that times out
// or returns a network error gets queued automatically.
export function attachOfflineInterceptor(api) {
  api.interceptors.response.use(
    (res) => res,
    (err) => {
      const cfg = err.config || {};
      const method = (cfg.method || "get").toLowerCase();
      const isMutating = ["post", "put", "patch", "delete"].includes(method);
      const isNetworkError = !err.response;
      if (isMutating && isNetworkError && cfg.url) {
        // Extract path: cfg.url may be absolute (axios baseURL+path)
        let urlPath = cfg.url;
        if (BACKEND_URL && urlPath.startsWith(BACKEND_URL)) urlPath = urlPath.slice(BACKEND_URL.length);
        offlineQueue.enqueue({
          method,
          url: urlPath,
          data: cfg.data ? (typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data) : undefined,
        });
        // Resolve gracefully with a synthetic offline response so the UI doesn't crash.
        return Promise.resolve({
          data: { ok: true, queued: true, offline: true },
          status: 202,
          statusText: "Queued (offline)",
          headers: { "x-hexa-queued": "1" },
          config: cfg,
        });
      }
      return Promise.reject(err);
    }
  );
}
